import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle,
  Shield,
  Target,
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  Activity,
  Brain,
  FileText,
  Eye,
  Download,
  Trash2,
  RefreshCw,
  Search,
  Filter,
  Plus,
  Edit,
  Copy,
  Share,
  Bookmark,
  Star,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Users,
  Building,
  Calendar,
  Settings,
  Database,
  Layers,
  Lock,
  Key,
  Fingerprint,
  Globe,
  MapPin,
  Phone,
  Mail,
  User,
  Save,
  X,
  CheckSquare,
  Square,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeft,
  Home,
  Wrench,
  FileBarChart,
  Cog,
  Link,
  Zap,
  Mic,
  Languages,
  Users2,
  Calculator,
  Receipt,
  CreditCard,
  Monitor,
  Tablet,
  Smartphone,
  Lightbulb,
  Cpu,
  Network,
  Info,
  ExternalLink,
  Archive,
  Send,
  MessageSquare,
  Flag,
  Heart,
  ThumbsUp,
  ThumbsDown,
  Upload,
  FileUp,
  FileDown,
  FileCheck,
  FileX,
  AlertCircle
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ActionMenu from '../common/ActionMenu';
import ConfirmationModal from '../common/ConfirmationModal';
import AIService from '../../services/aiService';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const ClauseRiskScoring = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isScoring, setIsScoring] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [riskThreshold, setRiskThreshold] = useState(0);
  const [selectedContract, setSelectedContract] = useState('all');
  const [selectedRiskLevel, setSelectedRiskLevel] = useState('all');

  // Mock data for clause risk scoring
  const [riskData, setRiskData] = useState([
    
  ]);

  const loadRiskOverview = async () => {
  try {
    const res = await AIService.getRiskOverview();

    if (res.success) {
      const mappedData = res.data.map((clause) => ({
        id: clause._id,
        clauseId: clause._id,
        clauseTitle: clause.title,
        clauseText: clause.content,
        
        contractName: 'Clause Library',
        riskCategory: clause.category || 'GENERAL',

        riskLevel: clause.riskLevel || 'Low',
        riskScore: clause.metadata?.riskScore || 0,
        riskFactors: clause.metadata?.riskFactors || [],
        mitigationStrategies: clause.metadata?.mitigationSuggestions || [],

        complianceStatus: clause.metadata?.riskScore >= 80
          ? 'Critical Risk'
          : clause.metadata?.riskScore >= 60
          ? 'Non-Compliant'
          : 'Compliant',

        financialImpact: '$0',
        confidence: clause.metadata?.confidence || 0,
        lastAnalyzed: clause.metadata?.lastAnalyzedAt || null,
        isActive: clause.isActive
      }));

      setRiskData(mappedData);
    }
  } catch (error) {
    console.error('Failed to load risk overview:', error);
  }
};


  useEffect(() => {
    loadRiskOverview();
  }, []);

  // Calculate statistics
 const stats = {
  totalClauses: riskData.length,
  highRiskClauses: riskData.filter(r => r.riskLevel === 'High' || r.riskLevel === 'Critical').length,
  criticalRiskClauses: riskData.filter(r => r.riskLevel === 'Critical').length,
  averageRiskScore: riskData.length
    ? riskData.reduce((sum, r) => sum + r.riskScore, 0) / riskData.length
    : 0,
  nonCompliantClauses: riskData.filter(r => r.complianceStatus !== 'Compliant').length
};

  // Filtered data
  const filteredRiskData = riskData.filter(risk => {
    const matchesContract = selectedContract === 'all' || risk.contractName === selectedContract;
    const matchesRiskLevel = selectedRiskLevel === 'all' || risk.riskLevel === selectedRiskLevel;
    const matchesThreshold = risk.riskScore >= riskThreshold;
    
    return matchesContract && matchesRiskLevel && matchesThreshold;
  });

  const tabs = [
    { id: 'overview', label: 'Risk Overview' },
    { id: 'analysis', label: 'Clause Analysis' },
    { id: 'scoring', label: 'Risk Scoring' },
    { id: 'mitigation', label: 'Mitigation Plans' },
    { id: 'reports', label: 'Risk Reports' }
  ];

  // Define columns for DataTable
  const columns = [
    {
      key: 'clauseTitle',
      label: 'Clause',
      width: '300px',
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}>{value}</div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.contractName} • {row.riskCategory}
          </div>
        </div>
      )
    },
    {
      key: 'riskLevel',
      label: 'Risk Level',
      width: '120px',
      render: (level, row) => {
        const levelConfig = {
          'Critical': { color: '#dc2626', bg: '#fee2e2', icon: <AlertTriangle size={14} /> },
          'High': { color: '#ea580c', bg: '#fed7aa', icon: <AlertTriangle size={14} /> },
          'Medium': { color: '#d97706', bg: '#fef3c7', icon: <AlertTriangle size={14} /> },
          'Low': { color: '#16a34a', bg: '#dcfce7', icon: <CheckCircle size={14} /> }
        };
        const config = levelConfig[level] || levelConfig['Medium'];
        
        return (
          <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: config.bg,
            color: config.color,
            width: 'fit-content'
          }}>
            {config.icon}
            {level}
          </span>
        );
      }
    },
    {
      key: 'riskScore',
      label: 'Risk Score',
      width: '120px',
      render: (score) => {
        const getScoreColor = (score) => {
          if (score >= 80) return '#dc2626';
          if (score >= 60) return '#ea580c';
          if (score >= 40) return '#d97706';
          return '#16a34a';
        };
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '60px',
              height: '6px',
              backgroundColor: '#e5e7eb',
              borderRadius: '3px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${score}%`,
                height: '100%',
                backgroundColor: getScoreColor(score),
                transition: 'width 0.3s ease'
              }} />
            </div>
            <span style={{ fontSize: '12px', fontWeight: '500', color: getScoreColor(score) }}>
              {score}
            </span>
          </div>
        );
      }
    },
    {
      key: 'financialImpact',
      label: 'Financial Impact',
      width: '120px',
      render: (impact) => (
        <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
          {impact}
        </div>
      )
    },
    {
      key: 'complianceStatus',
      label: 'Compliance',
      width: '120px',
      render: (status) => {
        const statusConfig = {
          'Critical Risk': { color: '#dc2626', bg: '#fee2e2' },
          'Non-Compliant': { color: '#ea580c', bg: '#fed7aa' },
          'Review Required': { color: '#d97706', bg: '#fef3c7' },
          'Compliant': { color: '#16a34a', bg: '#dcfce7' }
        };
        const config = statusConfig[status] || statusConfig['Review Required'];
        
        return (
          <span style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: config.bg,
            color: config.color
          }}>
            {status}
          </span>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '200px',
      render: (value, row) => (
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            onClick={() => handleView(row)}
            style={{
              padding: '6px 10px',
              fontSize: '12px',
              borderRadius: '6px',
              borderColor: 'var(--primary-color)',
              color: 'var(--primary-color)',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="View Risk Analysis"
          >
            <Eye size={14} /> View
          </button>
          <button
            onClick={() => handleAnalyze(row)}
            style={{
              padding: '6px 10px',
              fontSize: '12px',
              borderRadius: '6px',
              borderColor: '#f59e0b',
              color: '#f59e0b',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Re-analyze Risk"
          >
            <RefreshCw size={14} /> Analyze
          </button>
          <button
            onClick={() => handleDelete(row)}
            style={{
              padding: '6px 10px',
              fontSize: '12px',
              borderRadius: '6px',
              borderColor: '#ef4444',
              color: '#ef4444',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Delete Risk Analysis"
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      )
    }
  ];

  // Handler functions
  const handleView = (risk) => {
    //console.log('Viewing risk analysis:', risk.clauseTitle);
  };

  const handleAnalyze = async (risk) => {
  setIsAnalyzing(true);
  try {
    const res = await AIService.analyzeClause(risk.clauseId);

    if (res.success) {
      // Refresh risk overview after analysis
      loadRiskOverview();
    }
  } catch (err) {
    console.error(err);
  } finally {
    setIsAnalyzing(false);
  }
};

  const handleDelete = (risk) => {
    setSelectedItem(risk);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      setRiskData(prev => prev.filter(item => item.id !== selectedItem.id));
      setShowDeleteModal(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Error deleting risk analysis:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkRiskScoring = async () => {
  setIsScoring(true);
  try {
    const res = await AIService.analyzeAllClauses();

    if (res.success) {
      loadRiskOverview();
    }
  } catch (err) {
    console.error(err);
  } finally {
    setIsScoring(false);
  }
};


  return (
    <div className="page-container">
      <PageBreadcrumb />
      <PageHeader 
        title="Clause Risk Scoring"
        description="AI-powered risk assessment, clause analysis, and intelligent risk scoring"
      />

      {/* Tabbed Navigation */}
      <div className="page-tabs">
        <div className="page-tabs-list">
          {tabs.map(tab => (
            <div key={tab.id} className="page-tab-item">
              <button
                className={`page-tab-link ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.id === 'overview' && <BarChart3 size={18} className="page-tab-icon" />}
                {tab.id === 'analysis' && <Brain size={18} className="page-tab-icon" />}
                {tab.id === 'scoring' && <Target size={18} className="page-tab-icon" />}
                {tab.id === 'mitigation' && <Shield size={18} className="page-tab-icon" />}
                {tab.id === 'reports' && <FileText size={18} className="page-tab-icon" />}
                {tab.label}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="page-stats-grid">
        <div className="page-kpi-card color-1">
          <div className="page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <div className="page-kpi-value">{stats.totalClauses}</div>
                <div className="page-kpi-label">Total Clauses</div>
              </div>
              <div className="page-kpi-icon-container">
                <FileText size={20} className="page-kpi-icon" />
              </div>
            </div>
          </div>
        </div>

        <div className="page-kpi-card color-2">
          <div className="page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <div className="page-kpi-value">{stats.highRiskClauses}</div>
                <div className="page-kpi-label">High Risk Clauses</div>
              </div>
              <div className="page-kpi-icon-container">
                <AlertTriangle size={20} className="page-kpi-icon" />
              </div>
            </div>
          </div>
        </div>
      </div>

        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #e5e7eb'
        }}>
          <div>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>Critical Risks</p>
            <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>{stats.criticalRiskClauses}</h2>
          </div>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: '#dc262620',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#dc2626'
          }}>
            <XCircle size={20} />
          </div>
        </div>


      {/* Risk Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          {/* Risk Distribution */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px',
            marginBottom: '32px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 16px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <PieChart size={20} style={{ color: '#dc2626' }} />
                Risk Distribution
              </h3>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#dc2626', marginBottom: '8px' }}>
                {stats.criticalRiskClauses}
              </div>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                Critical risk clauses requiring immediate attention
              </p>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 16px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Shield size={20} style={{ color: '#16a34a' }} />
                Compliance Status
              </h3>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#16a34a', marginBottom: '8px' }}>
                {stats.nonCompliantClauses}
              </div>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                Non-compliant clauses requiring review
              </p>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 16px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Target size={20} style={{ color: 'var(--primary-color)' }} />
                Avg. Risk Score
              </h3>
              <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--primary-color)', marginBottom: '8px' }}>
                {Math.round(stats.averageRiskScore)}
              </div>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                Average risk score across all clauses
              </p>
            </div>
          </div>

          {/* Risk Data Table */}
          <div className="page-table-container">
            <DataTable
              data={filteredRiskData}
              columns={columns}
              title="Clause Risk Scoring"
              searchable={true}
              sortable={true}
              exportable={true}
              pagination={true}
              pageSize={10}
              showActions={true}
              showCheckboxes={false}
              onView={handleView}
              onEdit={handleAnalyze}
              onDelete={handleDelete}
              searchPlaceholder="Search clauses by title, contract, or risk level..."
              emptyMessage="No risk analysis data found. Upload contracts to analyze clauses."
              initialHiddenColumns={['id']}
            />
          </div>
        </div>
      )}

      {/* Clause Analysis Tab */}
      {activeTab === 'analysis' && (
        <div>
          {/* Analysis Controls */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: 0
              }}>
                Clause Analysis
              </h3>
              <div style={{ display: 'flex', gap: '12px' }}>
                <select
                  value={selectedContract}
                  onChange={(e) => setSelectedContract(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                >
                  <option value="all">All Contracts</option>
                  <option value="CONTRACT-001">IT Services Agreement</option>
                  <option value="CONTRACT-002">Software License Agreement</option>
                  <option value="CONTRACT-003">Development Services Contract</option>
                </select>
                <button
                  onClick={handleBulkRiskScoring}
                  disabled={isScoring}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    backgroundColor: isScoring ? '#9ca3af' : 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: isScoring ? 'not-allowed' : 'pointer',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                >
                  {isScoring ? (
                    <>
                      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Brain size={16} />
                      Analyze All
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Risk Threshold Slider */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Risk Threshold: {riskThreshold}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={riskThreshold}
                onChange={(e) => setRiskThreshold(parseInt(e.target.value))}
                style={{
                  width: '100%',
                  height: '6px',
                  borderRadius: '3px',
                  background: '#e5e7eb',
                  outline: 'none',
                  appearance: 'none'
                }}
              />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '12px',
                color: '#6b7280',
                marginTop: '4px'
              }}>
                <span>Low Risk (0%)</span>
                <span>High Risk (100%)</span>
              </div>
            </div>
          </div>

          {/* Detailed Risk Analysis */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '24px'
          }}>
            {filteredRiskData.map((risk) => (
              <div key={risk.id} style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '16px'
                }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1f2937',
                    margin: 0,
                    flex: 1
                  }}>
                    {risk.clauseTitle}
                  </h4>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backgroundColor: risk.riskLevel === 'Critical' ? '#fee2e2' : 
                                   risk.riskLevel === 'High' ? '#fed7aa' : 
                                   risk.riskLevel === 'Medium' ? '#fef3c7' : '#dcfce7',
                    color: risk.riskLevel === 'Critical' ? '#dc2626' : 
                           risk.riskLevel === 'High' ? '#ea580c' : 
                           risk.riskLevel === 'Medium' ? '#d97706' : '#16a34a'
                  }}>
                    {risk.riskLevel}
                  </span>
                </div>

                <div style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  marginBottom: '16px',
                  lineHeight: '1.5'
                }}>
                  {risk.clauseText.substring(0, 150)}...
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <h5 style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    margin: '0 0 8px 0'
                  }}>
                    Risk Factors:
                  </h5>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {risk.riskFactors.map((factor, index) => (
                      <li key={index} style={{
                        fontSize: '14px',
                        color: '#6b7280',
                        marginBottom: '4px'
                      }}>
                        {factor}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h5 style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    margin: '0 0 8px 0'
                  }}>
                    Mitigation Strategies:
                  </h5>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {risk.mitigationStrategies.map((strategy, index) => (
                      <li key={index} style={{
                        fontSize: '14px',
                        color: '#6b7280',
                        marginBottom: '4px'
                      }}>
                        {strategy}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risk Scoring Tab */}
      {activeTab === 'scoring' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          minHeight: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          color: '#6b7280'
        }}>
          Advanced Risk Scoring Dashboard Coming Soon!
        </div>
      )}

      {/* Mitigation Plans Tab */}
      {activeTab === 'mitigation' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          minHeight: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          color: '#6b7280'
        }}>
          Mitigation Plans Dashboard Coming Soon!
        </div>
      )}

      {/* Risk Reports Tab */}
      {activeTab === 'reports' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          minHeight: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          color: '#6b7280'
        }}>
          Risk Reports Dashboard Coming Soon!
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Risk Analysis"
        message={`Are you sure you want to delete the risk analysis for "${selectedItem?.clauseTitle}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};


export default ClauseRiskScoring;